import { useEffect, useRef, useState } from "react";

/**
 * Badge compacto que muestra el perfil de tarifas con popover al hacer click
 * - Mantiene tu lógica de agrupación y estado
 * - Popover con auto-placement (arriba si no cabe abajo)
 * - Contenido: Móvil / Fijo / Buzón (si existe) mostrando precio y rango
 */
export default function PerfilTarifasBadge({ perfilNombre, tarifas = [] }) {
    const [showPopover, setShowPopover] = useState(false);

    // --- helpers visuales ---
    const WIDTH = 280;       // ancho del popover
    const MAX_H = 240;       // altura máxima del contenido con scroll
    const anchorRef = useRef(null);
    const [placement, setPlacement] = useState("bottom"); // "bottom" | "top"
    const [hOffset, setHOffset] = useState(0);            // corrección si se sale por la derecha

    useEffect(() => {
        if (!showPopover) return;

        const compute = () => {
            const a = anchorRef.current?.getBoundingClientRect();
            if (!a) return;

            const desired = Math.min(MAX_H + 80, 320); // altura aprox. necesaria (contenido + header)
            const spaceBelow = window.innerHeight - a.bottom;
            const spaceAbove = a.top;

            setPlacement(spaceBelow < desired && spaceAbove > spaceBelow ? "top" : "bottom");

            const spaceRight = window.innerWidth - a.right;
            const overflowRight = WIDTH - spaceRight - 8; // margen seguridad
            setHOffset(overflowRight > 0 ? overflowRight : 0);
        };

        compute();
        const onResize = () => compute();
        const onScroll = () => compute();
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [showPopover]);

    // Si no hay tarifas, estado vacío
    if (!tarifas || tarifas.length === 0) {
        return <div className="text-[10px] text-violet-600 italic">Sin tarifas</div>;
    }

    // --- LÓGICA ORIGINAL: agrupar por tipo (SE MANTIENE) ---
    const grouped = {
        M: tarifas.filter((t) => t.type === "M"),
        F: tarifas.filter((t) => t.type === "F"),
        B: tarifas.filter((t) => t.type === "B"),
        FLAT: tarifas.filter((t) => t.type === "FLAT"),
    };

    const hasMovil = grouped.M.length > 0;
    const hasFijo = grouped.F.length > 0;
    const hasBuzon = grouped.B.length > 0;
    const hasFlat = grouped.FLAT.length > 0;

    // Tomar “principal” por tipo: menor rangeMin; empate -> menor precio
    const pickPrimary = (arr = []) =>
        !arr.length ? null : [...arr].sort((a, b) => (a.rangeMin - b.rangeMin) || (a.price - b.price))[0];

    const mov = pickPrimary(grouped.M);
    const fij = pickPrimary(grouped.F);
    const buz = pickPrimary(grouped.B);

    const money4 = (n) =>
        (n ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 4 });

    const Row = ({ icon, label, color, pill, data }) => (
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2 py-1.5">
            <div className="flex items-center gap-2 min-w-0">
                <span className={color}>{icon}</span>
                <span className={`font-semibold ${color} text-sm`}>{label}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <span className="tabular-nums text-slate-900 text-sm">{money4(data.price)}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${pill}`}>
                    {data.rangeMin}-{data.rangeMax}
                </span>
            </div>
        </div>
    );

    return (
        <div className="relative">
            {/* Badge compacto */}
            <button
                ref={anchorRef}
                onClick={() => setShowPopover(!showPopover)}
                className="w-full group"
                type="button"
            >
                <div className="bg-violet-50 hover:bg-violet-100 transition-all rounded px-1.5 py-1 mb-1 border border-violet-200 hover:border-violet-300">
                    <div className="flex items-center justify-between gap-1.5">
                        <div className="flex items-center gap-1 min-w-0">
                            <span className="text-violet-600 text-[10px]">📋</span>
                            <span className="text-[10px] font-semibold text-violet-900 truncate">
                                {perfilNombre || "Perfil de Tarifas"}
                            </span>
                        </div>
                        <svg
                            className={`w-2.5 h-2.5 text-violet-400 group-hover:text-violet-600 transition-all flex-shrink-0 ${showPopover ? "rotate-180" : ""
                                }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                {/* Indicadores compactos */}
                <div className="flex justify-center gap-0.5 text-[9px]">
                    {hasMovil && (
                        <span className="bg-violet-100 text-violet-700 px-1 py-0.5 rounded" title="Móvil">
                            📱
                        </span>
                    )}
                    {hasFijo && (
                        <span className="bg-violet-100 text-violet-700 px-1 py-0.5 rounded" title="Fijo">
                            ☎️
                        </span>
                    )}
                    {hasBuzon && (
                        <span className="bg-violet-100 text-violet-700 px-1 py-0.5 rounded" title="Buzón">
                            🗳️
                        </span>
                    )}
                    {hasFlat && (
                        <span className="bg-violet-100 text-violet-700 px-1 py-0.5 rounded" title="Flat">
                            🧾
                        </span>
                    )}
                </div>
            </button>

            {/* Popover con auto-placement (top/bottom) */}
            {showPopover && (
                <>
                    {/* Overlay */}
                    <div className="fixed inset-0 z-40" onClick={() => setShowPopover(false)} />

                    {/* Card compacta, CENTRADA y CHICA */}
                    <div
                        className="absolute z-50 left-1/2 top-full mt-1 w-[220px] transform -translate-x-1/2
                 rounded-xl border border-violet-200 bg-white shadow-xl"
                    >
                        {/* Flecha centrada */}
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3
                      bg-white border-l border-t border-violet-200 rotate-45" />
                        {/* CONTENIDO compacto (caben 3 filas) */}
                        <div className="p-2 space-y-1.5 max-h-[220px] overflow-y-auto">
                            {/* MÓVIL */}
                            {grouped.M.length > 0 && (() => {
                                const mov = [...grouped.M].sort((a, b) => (a.rangeMin - b.rangeMin) || (a.price - b.price))[0];
                                return (
                                    <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[13px]">📱</span>
                                            <span className="text-[12px] font-semibold text-violet-700">Móvil</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[12px] tabular-nums text-slate-900">
                                                {(mov.price ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 4 })}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
                                                {mov.rangeMin}-{mov.rangeMax}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* FIJO */}
                            {grouped.F.length > 0 && (() => {
                                const fij = [...grouped.F].sort((a, b) => (a.rangeMin - b.rangeMin) || (a.price - b.price))[0];
                                return (
                                    <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[13px]">☎️</span>
                                            <span className="text-[12px] font-semibold text-rose-700">Fijo</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[12px] tabular-nums text-slate-900">
                                                {(fij.price ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 4 })}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">
                                                {fij.rangeMin}-{fij.rangeMax}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* BUZÓN (si existe) */}
                            {grouped.B.length > 0 && (() => {
                                const buz = [...grouped.B].sort((a, b) => (a.rangeMin - b.rangeMin) || (a.price - b.price))[0];
                                return (
                                    <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[13px]">🗳️</span>
                                            <span className="text-[12px] font-semibold text-amber-700">Buzón</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[12px] tabular-nums text-slate-900">
                                                {(buz.price ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 4 })}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                                                {buz.rangeMin}-{buz.rangeMax}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
